import type { ISwivuProject } from "@/types/SwivuProject";
import { STORE_KEYS } from "@/other/constants/StoreKeys.jsx";
import type { BoardContext } from "@/bryntum-lib/models/BoardContext";
import type { BoardContextGroup } from "@/bryntum-lib/models/BoardContextGroup";
import type { BoardContextStatement } from "@/bryntum-lib/models/BoardContextStatement";
import { buildDuplicateContextName } from "./contextDisplayName";

const sameId = (a: number | string | null | undefined, b: number | string | null | undefined) =>
  Number(a) === Number(b);

const getGroupsForContext = (
  groupStore: { records: unknown[] },
  contextId: number,
  previewOnly = false
): BoardContextGroup[] =>
  (groupStore.records as unknown as BoardContextGroup[])
    .filter(
      (g) =>
        sameId(g.boardContext, contextId) && (previewOnly ? g.isPreview : !g.isPreview)
    )
    .sort((a, b) => a.order - b.order);

export async function duplicateBoardContextRecords(
  project: ISwivuProject,
  sourceId: number,
  boardId: number
): Promise<number> {
  const contextStore = project.getCrudStore(STORE_KEYS.boardContextStore);
  const groupStore = project.getCrudStore(STORE_KEYS.boardContextGroupStore);
  const statementStore = project.getCrudStore(STORE_KEYS.boardContextStatementStore);

  if (!contextStore || !groupStore || !statementStore) {
    throw new Error("Board context stores not available");
  }

  const source = contextStore.getById(sourceId) as unknown as BoardContext | null;
  if (!source) {
    throw new Error("Source context not found");
  }

  const sourceGroups = getGroupsForContext(groupStore, sourceId);
  const sourceGroupIds = new Set(sourceGroups.map((g) => g.id));
  const statementsToCopy = (statementStore.records as unknown as BoardContextStatement[])
    .filter((s) => !s.isPreview && sourceGroupIds.has(s.group))
    .sort((a, b) => a.order - b.order);

  const isCompletedSource = source.status === "completed";
  const duplicateName = buildDuplicateContextName(source.name);
  const scalarFields = {
    name: duplicateName,
    websiteUrl: source.websiteUrl,
    businessDescription: source.businessDescription,
    privateDocument: source.privateDocument,
    selectedUrls: [...(source.selectedUrls ?? [])],
    draftQuestions: isCompletedSource ? null : source.draftQuestions,
    contextHealth: source.contextHealth,
    status: source.status,
    isActive: false,
  };

  let newContextRecord: BoardContext | null = null;

  try {
    const added = contextStore.add({
      board: boardId,
      ...scalarFields,
    });

    newContextRecord = (Array.isArray(added) ? added[0] : added) as unknown as BoardContext;

    await project.sync();

    // Re-apply scalars after create — server may strip fields on initial insert.
    newContextRecord.set(scalarFields);
    await project.sync();

    const newContextId = newContextRecord.id;

    if (sourceGroups.length > 0) {
      for (const group of sourceGroups) {
        groupStore.add({
          boardContext: newContextId,
          title: group.title,
          order: group.order,
          isPreview: false,
        });
      }

      await project.sync();

      const newGroups = getGroupsForContext(groupStore, newContextId);

      const groupIdMap = new Map<number, number>();
      for (const src of sourceGroups) {
        const match = newGroups.find((ng) => ng.order === src.order && ng.title === src.title);
        if (match) groupIdMap.set(src.id, match.id);
      }

      for (const stmt of statementsToCopy) {
        const newGroupId = groupIdMap.get(stmt.group);
        if (newGroupId == null) continue;
        statementStore.add({
          group: newGroupId,
          headline: stmt.headline,
          body: stmt.body,
          order: stmt.order,
          isPreview: false,
        });
      }

      if (statementsToCopy.length > 0) {
        await project.sync();
      }
    }

    const persisted = contextStore.getById(newContextId) as unknown as BoardContext | null;
    if (persisted && persisted.name !== duplicateName) {
      persisted.set({ name: duplicateName });
      await project.sync();
    }

    return newContextId;
  } catch (err) {
    if (newContextRecord) {
      try {
        newContextRecord.remove();
        await project.sync();
      } catch {
        // Best-effort rollback if child sync failed after context was persisted.
      }
    }
    throw err;
  }
}
